import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import type { VendorEconomicsDetail } from '../../../common/models/unit-economics.model';
import { money } from '../costs/cost-format';
import { monthLabel } from './ue-format';

/** Revenue against cost, month by month: ink for what they paid, grey for what they cost. */
export function HistoryChart({ history }: { history: VendorEconomicsDetail['history'] }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const config: ChartConfig = {
    revenue: { label: t('UNIT_ECONOMICS.DRAWER.REVENUE'), color: 'var(--foreground)' },
    cost: { label: t('UNIT_ECONOMICS.DRAWER.COST'), color: 'var(--muted-foreground)' },
  };
  const data = history.map((h) => ({
    month: h.month,
    label: `${monthLabel(h.month, lang, true)}${h.partial ? '*' : ''}`,
    revenue: h.revenueUsd ?? 0,
    cost: h.costs.totalUsd,
    margin: h.marginUsd,
    partial: h.partial,
  }));

  return (
    <ChartContainer config={config} className="aspect-auto h-52 w-full" data-testid="ue-history-chart">
      <BarChart data={data} margin={{ left: -8, right: 4, top: 8, bottom: 0 }} barGap={2}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
        <YAxis tickLine={false} axisLine={false} width={52} tickFormatter={(v) => `$${Math.round(v)}`} />
        <ChartTooltip
          cursor={{ fill: 'var(--muted)', opacity: 0.5 }}
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) => {
                const p = payload?.[0]?.payload as (typeof data)[number] | undefined;
                if (!p) return '';
                const margin = p.margin == null ? '' : ` · ${t('UNIT_ECONOMICS.DRAWER.MARGIN')} ${money(p.margin)}`;
                return `${monthLabel(p.month, lang)}${p.partial ? ` (${t('UNIT_ECONOMICS.DRAWER.SO_FAR')})` : ''}${margin}`;
              }}
              formatter={(value, name) => (
                <div className="flex w-full items-center justify-between gap-3">
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <span className="size-2 rounded-[2px]" style={{ background: `var(--color-${name})` }} />
                    {config[name as 'revenue' | 'cost']?.label}
                  </span>
                  <span className="font-medium tabular-nums">{money(Number(value))}</span>
                </div>
              )}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Bar dataKey="revenue" fill="var(--color-revenue)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
        <Bar dataKey="cost" fill="var(--color-cost)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </ChartContainer>
  );
}
